import React, { useEffect, useState } from 'react';
import { Add, Delete, Details, Download, Edit } from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { routesName } from '../../constants/routes-name';
import { ApiManager } from '../../services/api';
import { IconButton, Pagination, Tooltip } from '@mui/material';
import useNotification from '../../hooks/useNotification';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import moment from 'moment';
import BreadcrumbsWrapper from '../../components/BreadcrumbsWrapper';
import useTenderSchedule from '../../hooks/useTenderSchedule';
import { useQuery } from '../../constants/shared-methods';
import { LightBlueButton } from '../../components';
import { DataGrid } from '@mui/x-data-grid';

function Component() {
  const query = useQuery();
  const page = query.get('page') || 1;
  const { user, role } = useSelector(({ user }) => user);
  const navigate = useNavigate();
  const { errorNonification, successNonification } = useNotification();
  const [tenders, setTenders] = useState();
  const [pagination, setPagination] = useState();

  const breadcrumbs = [
    { title: 'Dashboard', path: routesName.dashboard },
    { title: 'All Tenders', path: routesName.tenderListing },
  ];

  useEffect(() => {
    getTenders(page);
  }, [page]);

  const getTenders = async (page = 1) => {
    const res = await ApiManager.getTenders(page);
    if (res?.status === 200 && res?.data?.result) {
      setTenders(res.data.result);
      setPagination(res.data.pagination);
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        if (result.isConfirmed) {
          Swal.fire({
            title: 'Enter your password',
            input: 'password',
            inputAttributes: {
              autocapitalize: 'off',
            },
            showCancelButton: true,
            confirmButtonText: 'Verify',
            showLoaderOnConfirm: true,
            preConfirm: async (password) => {
              const loginUser = await ApiManager.verifyPassword({ password });
              if (loginUser.status === 200) {
                const res = await ApiManager.deleteTenderById(id);

                if (res.status === 200) {
                  getTenders(page);
                  successNonification('Tender has been deleted.');
                } else {
                  errorNonification(
                    res?.data?.message || 'Something went wrong'
                  );
                }
              } else {
                errorNonification(loginUser?.data?.message || 'Wrong password');
              }
            },
            allowOutsideClick: () => !Swal.isLoading(),
          });
        }
      }
    });
  };

  const columns = [
    {
      field: 'tenderTitle',
      headerName: 'Title',
      minWidth: 200,
      flex: 1,
      sortable: false,
    },
    {
      field: 'organization',
      headerName: 'Organisation',
      minWidth: 80,
      flex: 0.7,
      sortable: false,
    },
    {
      field: 'publicationDate',
      headerName: 'Published',
      minWidth: 80,
      flex: 0.5,
      sortable: false,
    },
    {
      field: 'submissionDate',
      headerName: 'Deadline',
      minWidth: 80,
      flex: 0.5,
      sortable: false,
    },
    {
      field: 'procedureType',
      headerName: 'Procedure Type',
      minWidth: 80,
      flex: 0.5,
      sortable: false,
    },
    {
      field: 'beneficiatyLocation',
      headerName: 'Country',
      minWidth: 80,
      flex: 0.5,
      sortable: false,
    },
    {
      field: 'action',
      headerName: 'Actions',
      description: 'This column has a value getter and is not sortable.',
      sortable: false,
      minWidth: 180,
      flex: 0.5,
      headerAlign: 'center',
      renderCell: (params) => {
        return (
          <div
            className="flex h-full  items-center justify-center text-[var(--gray)]"
            onClick={(e) => e.stopPropagation()}
          >
            {(user._id === params.row?.userKey1?._id ||
              user._id === params.row?.userKey2?._id ||
              user._id === params.row?.userKey3?._id) && (
              <DetailIcon tenderData={params.row} />
            )}

            {role?.id !== 5 && (
              <Tooltip title="Edit" arrow>
                <IconButton
                  onClick={() =>
                    navigate(`${routesName.editTenderPath}${params.row._id}`)
                  }
                  size="small"
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {role?.id !== 5 && (
              <Tooltip title="Delete" arrow>
                <IconButton
                  onClick={() => handleDelete(params.row._id)}
                  size="small"
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  const rows =
    tenders &&
    Array.isArray(tenders) &&
    tenders.length > 0 &&
    tenders.map((item, index) => ({
      id: item?._id,
      _id: item?._id,
      beneficiatyLocation: item?.beneficiatyLocation || ``,

      submissionDate: item?.submissionDate
        ? moment(item.submissionDate).format('DD-MM-YYYY')
        : '',
      procedureType: item?.procedureType || '',
      publicationDate: item?.publicationDate
        ? moment(item.publicationDate).format('DD-MM-YYYY')
        : '',
      publicationTime: item?.publicationTime | '',
      refNo: item?.refNo || '',
      status: item?.status,
      submissionTime: item?.submissionTime,
      tenderTitle: item?.tenderTitle || '',
      organization: item?.organization?.name || '',
    }));

  return (
    <div className="flex flex-col gap-8">
      <BreadcrumbsWrapper breadcrumbs={breadcrumbs} />
      <div className="flex flex-col gap-3">
        <div className="flex justify-between items-center">
          <div className="text-xl font-bold">Tender</div>
          {role?.id !== 5 && (
            <Link to={routesName.createTender}>
              <LightBlueButton>
                <Add className="text-white" fontSize="small" />
                Add Tender
              </LightBlueButton>
            </Link>
          )}
        </div>

        {rows && (
          <div>
            <DataGrid
              sx={{
                '& .MuiDataGrid-columnHeaders div': {
                  backgroundColor: 'var(--blue) !important',
                  textAlign: 'center !important',
                  color: 'white !important',
                },
                '& .MuiDataGrid-virtualScrollerContent': {
                  backgroundColor: 'white !important',
                },
                '& .MuiDataGrid-row': {
                  cursor: 'pointer !important',
                },
              }}
              onRowClick={(params) => {
                navigate(`${routesName.viewTenderPath}${params.id}`);
              }}
              rows={rows}
              columns={columns}
              autoHeight
              disableColumnMenu
              //disableColumnSorting
              //disableColumnResize
              hideFooterSelectedRowCount
              hideFooter
            />
            {
              <Pagination
                variant="outlined"
                color="primary"
                page={pagination?.currentPage || 1}
                component="div"
                onChange={(event, newPage) => {
                  navigate(
                    `${routesName.supplierAllTenderListing}?page=${newPage}`
                  );
                }}
                count={pagination?.totalPage || 1}
                className="flex-shrink-0 border-t-1 flex justify-center mt-2"
              />
            }
          </div>
        )}

        {/*  {Array.isArray(tenders) && tenders.length > 0 && (
          <div className="flex flex-col gap-3 py-6">
            <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
              <table className="w-full text-sm text-left rtl:text-right text-gray-500">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-nowrap">
                      ID
                    </th>
                    <th scope="col" className="px-6 py-3 text-nowrap">
                      Tender Title
                    </th>
                    <th scope="col" className="px-6 py-3 text-nowrap">
                      Due Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-nowrap">
                      Reference No
                    </th>
                    <th scope="col" className="px-6 py-3 text-nowrap">
                      Procedure Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-nowrap">
                      Publication Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-nowrap">
                      End Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-nowrap">
                      Procurement Category (code)
                    </th>
                    <th scope="col" className="px-6 py-3 text-nowrap">
                      Country
                    </th>
                    <th scope="col" className="px-6 py-3 text-nowrap">
                      No of downloads
                    </th>
                    <th scope="col" className="px-6 py-3 text-nowrap">
                      No of submissions
                    </th>
                    <th scope="col" className="px-6 py-3 text-nowrap">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(tenders) &&
                    tenders.map((item, index) => (
                      <tr
                        key={index}
                        className="odd:bg-white even:bg-gray-50 border-b"
                      >
                        <th className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">
                          {item?._id}
                        </th>
                        <th
                          scope="row"
                          className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap"
                        >
                          {item?.tenderTitle}
                        </th>
                        <td className="px-6 py-4">
                          {item?.submissionDate
                            ? moment(item?.submissionDate).format('DD-MM-YYYY')
                            : ''}
                        </td>
                        <td className="px-6 py-4">{item?.refNo}</td>
                        <td className="px-6 py-4">{item?.procedureType}</td>
                        <td className="px-6 py-4">
                          {item?.publicationDate
                            ? moment(item?.publicationDate).format('DD-MM-YYYY')
                            : ''}
                        </td>
                        <td className="px-6 py-4">
                          {item?.deadlineForQueries
                            ? moment(item?.deadlineForQueries).format(
                                'DD-MM-YYYY'
                              )
                            : ''}
                        </td>
                        <td className="px-6 py-4">{item?.unspscAndCpvCode}</td>
                        <td className="px-6 py-4">
                          {item?.beneficiatyLocation}
                        </td>
                        <td className="px-6 py-4">{0}</td>
                        <td className="px-6 py-4">{0}</td>
                        <td className="px-6 py-4">
                          <div className="text-sm flex gap-2">
                            {(user._id === item?.userKey1?._id ||
                              user._id === item?.userKey2?._id ||
                              user._id === item?.userKey3?._id) && (
                              <DetailIcon tenderData={item} />
                            )}

                            <Tooltip title="Click to see details" arrow>
                              <IconButton
                                onClick={() =>
                                  navigate(
                                    `${routesName.viewTenderPath}${item._id}`
                                  )
                                }
                                size="small"
                              >
                                <Details fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            {role?.id !== 5 && (
                              <Tooltip title="Edit" arrow>
                                <IconButton
                                  onClick={() =>
                                    navigate(
                                      `${routesName.editTenderPath}${item._id}`
                                    )
                                  }
                                  size="small"
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {role?.id !== 5 && (
                              <Tooltip title="Delete" arrow>
                                <IconButton
                                  onClick={() => handleDelete(item._id)}
                                  size="small"
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <Pagination
              variant="outlined"
              color="primary"
              page={pagination?.currentPage || 1}
              component="div"
              onChange={(event, newPage) => {
                navigate(`${routesName.tenderListing}?page=${newPage}`);
              }}
              count={pagination?.totalPage || 1}
              className="flex-shrink-0 border-t-1 flex justify-center mt-2"
            />
          </div>
        )} */}
      </div>
    </div>
  );
}

export default Component;

const DetailIcon = ({ tenderData }) => {
  const {
    timeRemaining,
    isBeforeSubmission,
    isAfterPublication,
    isBeforePublication,
    isAfterSubmission,
    isAfterQueryDeadline,
  } = useTenderSchedule(tenderData);
  const navigate = useNavigate();

  if (isBeforeSubmission) {
    return (
      <Tooltip title={'Downlod Bids'} arrow>
        <IconButton
          onClick={() =>
            navigate(`${routesName.downloadBidPath}${tenderData._id}`)
          }
          size="small"
        >
          <Download fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }
  return null;
};
